// The dev plane's multi-repo GOAL (plan 101 slice 12): one plan, three repos, every leg fact
// the epic view renders — a sibling module for the 300-line ceiling the other story files
// sit against (`mock-size.test.ts`).
//
// It declares STORED truth only: the edges a writer stated and the newest deploy verdict per
// environment. `released`, the deploy chips, `mergeHeld` and the plan's `goalState` are the
// dispatcher's derivation (`plan-leg-mocks.ts`), so this story cannot claim a release the
// server would not judge from the same rows:
//   - infrastructure MERGED, a success verdict in staging and none yet in production, which
//     product's `deployed: production` edge awaits — so production reads `pending`;
//   - product is held at START on that edge (`upstream_leg_undeployed`);
//   - customer is in review with an open PR, its MERGE held on product (`hold: merge`).

import { planGroupDisposition } from '@developerz/domain';
import { at } from './mock-session';
import type { StoryGroup } from './plan-leg-mocks';
import type { PlanStory } from './plan-stories-mocks';

const INFRA_SHA = '9f3c2a17b0d84e6c5a1f2b3c4d5e6f708192a3b4';

/** Disposition DERIVED by the classifier the server uses, never typed (see the sibling stories). */
function leg(input: Omit<StoryGroup, 'disposition' | 'stallReason' | 'noWork'>): StoryGroup {
  const verdict = planGroupDisposition({
    groupStatus: input.status,
    task:
      input.task === null
        ? null
        : {
            status: input.task.status,
            pullRequestNumber: input.task.pullRequestNumber,
            outcomeKind: 'pr',
            noWorkSettled: false,
            opsActionRefused: false,
          },
    pullRequestState: input.pullRequest?.state ?? null,
  });
  return { ...input, ...verdict, noWork: null };
}

function goalGroups(): StoryGroup[] {
  return [
    leg({
      groupId: 'grp_infra',
      position: 0,
      title: 'Provision the billing queue',
      repoFullName: 'developerz-ai/infrastructure',
      status: 'merged',
      dependsOn: [],
      verdicts: [{ environment: 'staging', conclusion: 'success' }],
      task: { id: 'tsk_goal_infra', status: 'merged', pullRequestNumber: 4301 },
      pullRequest: {
        number: 4301,
        state: 'merged',
        title: 'Provision the billing queue',
        mergeable: null,
        mergeDecision: 'auto',
        mergeCommitSha: INFRA_SHA,
      },
      heldOn: null,
    }),
    leg({
      groupId: 'grp_product',
      position: 1,
      title: 'Publish invoices onto the queue',
      repoFullName: 'developerz-ai/api-gateway',
      status: 'dispatched',
      dependsOn: ['grp_infra'],
      edgeSpecs: [
        { upstream: 'grp_infra', release: 'deployed', environment: 'production', hold: 'start' },
      ],
      task: { id: 'tsk_goal_product', status: 'pending', pullRequestNumber: null },
      pullRequest: null,
      heldOn: { reason: 'upstream_leg_undeployed', pullRequestNumber: null },
    }),
    leg({
      groupId: 'grp_customer',
      position: 2,
      title: 'Show invoice status to customers',
      repoFullName: 'developerz-ai/web-ui',
      status: 'dispatched',
      dependsOn: ['grp_product'],
      edgeSpecs: [{ upstream: 'grp_product', release: 'merged', environment: null, hold: 'merge' }],
      task: { id: 'tsk_goal_customer', status: 'awaiting_review', pullRequestNumber: 4302 },
      pullRequest: {
        number: 4302,
        state: 'open',
        title: 'Show invoice status to customers',
        mergeable: 'mergeable',
        mergeDecision: null,
        mergeCommitSha: null,
      },
      heldOn: null,
    }),
  ];
}

export const GOAL_PLAN_STORY: PlanStory = {
  planId: 'plan_mock_goal',
  status: 'open',
  rawTaskId: 'rtk_mock_goal',
  initiatedBy: 'system',
  owner: null,
  createdIso: at('2026-07-18'),
  updatedIso: at('2026-07-21'),
  groups: goalGroups(),
};
